import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { useCanAccess, useNotify } from 'react-admin';
import { useLocation } from 'react-router-dom';

import {
    type ReceiptCategory,
    type ResponsibleUser,
    assignTransactionUser,
    fetchReceiptCategories,
    fetchReconciliationSummary,
    fetchResponsibleUsers,
    importExpenseReports,
    matchReceipt,
    reconcileReceipts,
    syncQuickBooksCharges,
    requestMissingReceipt,
    reviewReceiptAllocation,
} from './receiptApi';
import { DailyReceiptsList } from './DailyReceiptsList';
import { QuickBooksConnectCard } from './QuickBooksConnectCard';
import { ReceiptPreviewDialog, type ReceiptPreviewTarget } from './ReceiptPreviewDialog';
import { ReconciliationPanel } from './ReconciliationPanel';
import { EMPTY_SUMMARY } from './receiptHelpers';

const MATCH_PATH = /^\/receipts\/admin\/match\/(\d+)\/?$/;

export const ReceiptAdminPage = () => {
    const notify = useNotify();
    const { pathname } = useLocation();
    const matchExpenseId = useMemo(() => {
        const matched = pathname.match(MATCH_PATH);
        return matched ? Number(matched[1]) : null;
    }, [pathname]);
    const { canAccess: canViewDaily } = useCanAccess({
        resource: 'receipt',
        action: 'view_daily_receipts',
    });
    const { canAccess: canAdminReconciliation } = useCanAccess({
        resource: 'receipt',
        action: 'admin_reconciliation',
    });

    const showDaily = !!canViewDaily;
    const showReconciliation = !!canAdminReconciliation;
    const [tab, setTab] = useState(0);
    const [categories, setCategories] = useState<ReceiptCategory[]>([]);
    const [users, setUsers] = useState<ResponsibleUser[]>([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState<ReceiptPreviewTarget | null>(null);

    // Default tab from permissions only — do not reset when match dialog closes.
    useEffect(() => {
        if (showDaily) setTab(0);
        else if (showReconciliation) setTab(1);
    }, [showDaily, showReconciliation]);

    // Deep-link / open match → force reconciliation tab without touching close.
    useEffect(() => {
        if (matchExpenseId && showReconciliation) {
            setTab(showDaily ? 1 : 0);
        }
    }, [matchExpenseId, showReconciliation, showDaily]);

    const refreshSummary = useCallback(async () => {
        if (!showReconciliation) {
            setSummary(EMPTY_SUMMARY);
            return;
        }
        setSummary(await fetchReconciliationSummary());
    }, [showReconciliation]);

    useEffect(() => {
        if (!showDaily && !showReconciliation) return;
        void Promise.all([
            fetchReceiptCategories(),
            fetchResponsibleUsers(),
            refreshSummary(),
        ])
            .then(([nextCategories, nextUsers]) => {
                setCategories(nextCategories);
                setUsers(nextUsers);
            })
            .catch(error => notify(error.message, { type: 'error' }));
    }, [notify, refreshSummary, showDaily, showReconciliation]);

    const runAction = async (
        action: () => Promise<unknown>,
        success: string
    ): Promise<boolean> => {
        setBusy(true);
        try {
            await action();
            await refreshSummary();
            notify(success, { type: 'success' });
            return true;
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Request failed',
                { type: 'error' }
            );
            return false;
        } finally {
            setBusy(false);
        }
    };

    if (!showDaily && !showReconciliation) {
        return (
            <Box py={3}>
                <Alert severity="warning">
                    You do not have permission to view receipt administration.
                </Alert>
            </Box>
        );
    }

    const visibleTabs = [
        ...(showDaily ? (['daily'] as const) : []),
        ...(showReconciliation ? (['reconciliation'] as const) : []),
    ];
    const activeKey = visibleTabs[Math.min(tab, visibleTabs.length - 1)];

    return (
        <Box py={3}>
            <Typography variant="h4" gutterBottom>
                Receipts admin
            </Typography>
            <Typography color="text.secondary" mb={2}>
                Review daily submissions and reconcile AMEX/Visa charges from
                QuickBooks Online.
            </Typography>
            {showReconciliation && <QuickBooksConnectCard />}
            <Tabs
                value={Math.min(tab, visibleTabs.length - 1)}
                onChange={(_, value) => setTab(value)}
                sx={{ mb: 3 }}
            >
                {showDaily && <Tab label="Daily receipts" />}
                {showReconciliation && <Tab label="Reconciliation" />}
            </Tabs>
            {activeKey === 'daily' ? (
                <DailyReceiptsList />
            ) : (
                <ReconciliationPanel
                    summary={summary}
                    users={users}
                    categories={categories}
                    busy={busy}
                    canAdmin={showReconciliation}
                    matchExpenseId={matchExpenseId}
                    onImport={() =>
                        runAction(async () => {
                            const result = await importExpenseReports();
                            if (result.errors.length) {
                                throw new Error(result.errors.join(' · '));
                            }
                        }, 'Local AMEX/Visa reports imported') as unknown as Promise<void>
                    }
                    onSyncQuickBooks={() =>
                        runAction(async () => {
                            const result = await syncQuickBooksCharges();
                            setSummary(result);
                        }, 'QuickBooks charges synced') as unknown as Promise<void>
                    }
                    onReconcile={() =>
                        runAction(reconcileReceipts, 'Reconciliation complete') as unknown as Promise<void>
                    }
                    onRequest={id =>
                        runAction(
                            () => requestMissingReceipt(id),
                            'Missing-receipt request sent'
                        ) as unknown as Promise<void>
                    }
                    onAssign={(transactionId, userId) =>
                        runAction(
                            () => assignTransactionUser(transactionId, userId),
                            'Responsible employee updated'
                        )
                    }
                    onMatch={(transactionId, receiptId) =>
                        runAction(
                            () => matchReceipt(transactionId, receiptId),
                            'Receipt matched'
                        )
                    }
                    onViewReceipt={async id => {
                        setPreview({
                            id,
                            original_filename: `receipt-${id}`,
                        });
                    }}
                    onReviewAllocation={(id, decision, changes) =>
                        runAction(async () => {
                            const receipt = await reviewReceiptAllocation(
                                id,
                                decision,
                                changes
                            );
                            if (receipt.qbo_push_error) {
                                throw new Error(
                                    `Verified in MRT, but QuickBooks push failed: ${receipt.qbo_push_error}`
                                );
                            }
                        }, decision === 'verified'
                            ? 'Verified and pushed to QuickBooks'
                            : 'Allocation returned to employee') as unknown as Promise<void>
                    }
                />
            )}
            <ReceiptPreviewDialog
                receipt={preview}
                onClose={() => setPreview(null)}
            />
        </Box>
    );
};
